import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface PresenceState {
  [key: string]: {
    user_id: string;
    email: string;
    online_at: string;
  }[];
}

interface PresencePayload {
  user_id: string;
  email: string;
  online_at: string;
}

const CHANNEL_NAME = 'app-presence';

// Singleton channel management
class PresenceManager {
  private static instance: PresenceManager | null = null;
  private channel: RealtimeChannel | null = null;
  private isSubscribed = false;
  private subscribers = new Set<(state: PresenceState) => void>();
  private pendingTrack: { userId: string; email: string } | null = null;

  static getInstance(): PresenceManager {
    if (!PresenceManager.instance) {
      PresenceManager.instance = new PresenceManager();
    }
    return PresenceManager.instance;
  }

  private getChannel(): RealtimeChannel {
    if (!this.channel) {
      console.log('[Presence] Creating channel');
      this.channel = supabase.channel(CHANNEL_NAME);
      
      this.channel
        .on('presence', { event: 'sync' }, () => {
          const state = this.channel!.presenceState() as PresenceState;
          console.log('[Presence] Sync:', Object.keys(state).length, 'users');
          this.notifySubscribers(state);
        })
        .on('presence', { event: 'join' }, ({ newPresences }) => {
          console.log('[Presence] Join:', newPresences);
          const state = this.channel!.presenceState() as PresenceState;
          this.notifySubscribers(state);
        })
        .on('presence', { event: 'leave' }, ({ leftPresences }) => {
          console.log('[Presence] Leave:', leftPresences);
          const state = this.channel!.presenceState() as PresenceState;
          this.notifySubscribers(state);
        });
    }
    return this.channel;
  }

  private notifySubscribers(state: PresenceState) {
    this.subscribers.forEach(callback => callback(state));
  }

  subscribe(callback: (state: PresenceState) => void) {
    this.subscribers.add(callback);
    const channel = this.getChannel();

    if (this.isSubscribed) {
      // Already subscribed, send current state
      callback(channel.presenceState() as PresenceState);
    } else if (this.subscribers.size === 1) {
      // First subscriber, start subscription
      channel.subscribe((status) => {
        console.log('[Presence] Channel status:', status);
        if (status === 'SUBSCRIBED') {
          this.isSubscribed = true;
          // Process any pending track
          if (this.pendingTrack) {
            this.doTrack(this.pendingTrack.userId, this.pendingTrack.email);
            this.pendingTrack = null;
          }
          // Notify all subscribers
          const state = channel.presenceState() as PresenceState;
          this.notifySubscribers(state);
        }
      });
    }

    return () => {
      this.subscribers.delete(callback);
      // Don't unsubscribe the channel - keep it alive
    };
  }

  private async doTrack(userId: string, email: string) {
    if (!this.channel || !this.isSubscribed) return;
    
    try {
      const result = await this.channel.track({
        user_id: userId,
        email: email,
        online_at: new Date().toISOString(),
      });
      console.log('[Presence] Tracked:', userId, result);
    } catch (err) {
      console.error('[Presence] Track error:', err);
    }
  }

  track(userId: string, email: string) {
    if (this.isSubscribed) {
      this.doTrack(userId, email);
    } else {
      // Queue for when channel is ready
      this.pendingTrack = { userId, email };
      // Ensure channel exists and is subscribing
      this.getChannel();
    }
  }

  untrack() {
    if (this.channel && this.isSubscribed) {
      this.channel.untrack();
    }
  }
}

export const usePresenceTracking = (userId: string | undefined, userEmail: string | undefined) => {
  const trackedRef = useRef(false);
  const manager = PresenceManager.getInstance();

  useEffect(() => {
    if (!userId || !userEmail) {
      return;
    }

    if (!trackedRef.current) {
      manager.track(userId, userEmail);
      trackedRef.current = true;
    }

    // Subscribe to keep channel alive (even if we don't use the state here)
    const unsubscribe = manager.subscribe(() => {});

    return () => {
      trackedRef.current = false;
      manager.untrack();
      unsubscribe();
    };
  }, [userId, userEmail, manager]);
};

export const useOnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const manager = PresenceManager.getInstance();

  const handleStateChange = useCallback((state: PresenceState) => {
    const userIds = new Set<string>();
    
    Object.values(state).forEach((presences) => {
      presences.forEach((presence) => {
        if (presence.user_id) {
          userIds.add(presence.user_id);
        }
      });
    });
    
    console.log('[Presence] Online users:', Array.from(userIds));
    setOnlineUsers(userIds);
  }, []);

  useEffect(() => {
    const unsubscribe = manager.subscribe(handleStateChange);
    return unsubscribe;
  }, [manager, handleStateChange]);

  return { onlineUsers };
};
